import { useEffect } from 'react';
import { useRouter } from 'next/router';

// Types
import type { NotificationCollaborationProps } from '@/utils/hooks/context/toast/types';
import type { SocialProfileType } from '@/types/socialProfileType';

// Routes
import { SCHEDULER } from '@/routes/privateRoutes';

// Hooks
import useUser from '@/hooks/swr/useUser';
import useFormatDateTime from '@/utils/hooks/format/useFormatDateTime';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useMarkAsReadNotification from '@/utils/hooks/notifications/useMarkAsReadNotification';
import useSelectedSocialProfileById from '@/utils/hooks/socialprofiles/useSelectedSocialProfileById';

// Component
import Icon from '@/components/base/Icon';
import Notification from '@/components/base/Notification';
import PostThumbnail from '@/components/composite/Notifications/Collaboration/components/PostThumbnail';

const PostCommented = ({ notification }: NotificationCollaborationProps) => {
    const { user } = useUser();

    const router = useRouter();
    const { setActiveSocialProfile } = useActiveSocialProfile();
    const { selectedSocialProfile } = useSelectedSocialProfileById(
        notification?.data?.social_profile_id as SocialProfileType['id']
    );
    const { markAsReadCollaborationNotification } = useMarkAsReadNotification();

    const { formatDateTime } = useFormatDateTime(user?.settings?.timezone ?? 'Europe/Paris');

    const url = `${SCHEDULER}?post_id=${notification?.data?.scheduler_post?.id}`;
    const userTimeFormat: '24' | '12' = user?.settings?.time_format ?? '24';

    // Prefetch Overview
    useEffect(() => {
        router.prefetch(url);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Notification
            onClick={() => {
                markAsReadCollaborationNotification(notification);
                if (selectedSocialProfile) {
                    setActiveSocialProfile(selectedSocialProfile);
                    router.push(url);
                }
            }}
            dataNotification={notification}
            contentHidden={notification?.options?.hideContent ?? false}
            wide={notification?.options?.wide ?? true}>
            <div className="relative">
                <PostThumbnail
                    thumbnail={notification.data?.scheduler_post?.media_thumbnail}
                    postType={notification.data?.scheduler_post?.type ?? 'image'}
                />
                <div className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-icoBlue text-8 text-white">
                    <Icon icon="conversations" />
                </div>
                <div className="absolute -bottom-1.5 -right-1.5">
                    <Notification.Avatar size="xs" socialProfile={selectedSocialProfile} />
                </div>
            </div>
            <Notification.Content>
                <Notification.Title
                    i18nKey="commons:notifications.scheduler_post_status_changed.title_commented"
                    values={{ name: notification.data.username }}
                />
                <Notification.Description
                    i18nKey="commons:notifications.scheduler_post_status_changed.description"
                    values={{
                        date: formatDateTime(
                            notification.data.scheduler_post.scheduled_at,
                            user?.settings?.date_format
                        ),
                        hour: formatDateTime(
                            notification.data.scheduler_post.scheduled_at,
                            `${userTimeFormat}_hour_minutes`
                        )
                    }}
                />
            </Notification.Content>
        </Notification>
    );
};

export default PostCommented;
